import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  formatDateTime,
  formatDuration,
  formatNumber
} from '../Shared/detailFormat'
import { isYardsWorkout } from '../CompletedWorkout/completedWorkoutFields'

const activityType = (completedWorkout) => {
  const workout = completedWorkout.get('workout')
  if (!workout) return '?'
  return workout.get('isAllStrength') ? 'Dryland' : 'Swim'
}

// Text the filter box matches against: what support usually has in hand
// (a title, a date, a device, a source, an id).
export const activitySearchText = (completedWorkout) => [
  completedWorkout.id,
  completedWorkout.get('title'),
  completedWorkout.get('workoutSource'),
  completedWorkout.get('modelName'),
  activityType(completedWorkout),
  completedWorkout.get('isGuided') ? 'guided' : 'unguided',
  formatDateTime(completedWorkout.get('dateCompleted'))
].filter(Boolean).join(' ').toLowerCase()

export const filterActivities = (workouts, filter) => {
  const terms = (filter || '').toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) {
    return workouts
  }
  return workouts.filter((completedWorkout) => {
    const text = activitySearchText(completedWorkout)
    return terms.every((term) => text.includes(term))
  })
}

const renderFitDownload = (file) => file
  ? <a href={file.url()} download className="btn btn-sm btn-secondary">Download</a>
  : null

// A member can have hundreds of activities, so the table scrolls inside a
// fixed-height box (header pinned) and a filter narrows it, rather than
// pushing every section below it off the page.
const CompletedActivitiesTableComponent = ({ workouts }) => {
  const [filter, setFilter] = useState('')
  const visible = useMemo(
    () => filterActivities(workouts || [], filter),
    [workouts, filter]
  )

  if (!workouts) return <i>Loading...</i>
  if (workouts.length === 0) return <i>No workouts</i>

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <input
          type="search"
          className="form-control form-control-sm detail-filter-input"
          placeholder="Filter by title, date, source, device, id"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <small className="text-muted text-nowrap">
          {visible.length === workouts.length
            ? `${workouts.length} activities`
            : `${visible.length} of ${workouts.length}`}
        </small>
      </div>
      <div className="detail-long-list">
        <table className="data">
          <thead>
            <tr className="data">
              <th className="bg-primary">Name</th>
              <th className="bg-primary">Duration</th>
              <th className="bg-primary">Distance</th>
              <th className="bg-primary">Date</th>
              <th className="bg-primary">Type</th>
              <th className="bg-primary">Source</th>
              <th className="bg-primary">Input .fit</th>
              <th className="bg-primary">Output .fit</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((completedWorkout) => {
              const workout = completedWorkout.get('workout')
              const isDryland = workout?.get('isAllStrength')
              return (
                <tr key={completedWorkout.id} className="data">
                  <td>
                    <a href={'/#/completedWorkouts/' + completedWorkout.id}>
                      <b>{completedWorkout.get('title')}</b><br/>
                      { completedWorkout.get('heartRateData')?.length ? <span title="HR samples">❤️</span> : '' }
                      { completedWorkout.get('lapSamplesData')?.length ? <span title="Lap samples">⏱️</span> : '' }
                      { completedWorkout.get('locationSamples')?.length ? <span title="GPS samples">🗺</span> : '' }
                      { workout?.get('isTestSetWorkout') ? <span title="Test Set">🏁</span> : '' }
                      { completedWorkout.get('isGenerated') ? <span title="Generated workout">🪄</span> : '' }
                      { completedWorkout.get('startLocation') ? <span title="Location">📍</span> : '' }
                      { completedWorkout.get('garminConnectWorkoutId')?.length ? <span title="Garmin Connect workout">📲</span> : '' }
                      { completedWorkout.get('isSplashModeRunner') ? <span title="Splash Mode">💦</span> : '' }
                      { completedWorkout.get('image') ? <span title="Photo">📸</span> : '' }
                    </a>
                  </td>
                  <td>{formatDuration(completedWorkout.get('totalTime'))}</td>
                  <td>
                    {!workout
                      ? '?'
                      : isDryland
                        ? 'N/A'
                        : formatNumber(completedWorkout.get('totalDistance'), 0) +
                          (isYardsWorkout(completedWorkout) ? ' yd' : ' m')}
                  </td>
                  <td>{formatDateTime(completedWorkout.get('dateCompleted'))}</td>
                  <td>
                    {activityType(completedWorkout)}
                    <br/>
                    <span className="text-muted">{completedWorkout.get('isGuided') ? 'Guided' : ''}</span>
                  </td>
                  <td className="small">
                    {completedWorkout.get('workoutSource')}
                    <div className="text-muted">{completedWorkout.get('modelName')}</div>
                  </td>
                  <td>{renderFitDownload(completedWorkout.get('sourceFITFile'))}</td>
                  <td>{renderFitDownload(completedWorkout.get('createdFITFile'))}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {visible.length === 0 && (
          <p className="text-muted p-2 mb-0">No activities match &quot;{filter}&quot;</p>
        )}
      </div>
    </div>
  )
}

CompletedActivitiesTableComponent.propTypes = {
  workouts: PropTypes.array
}

export default CompletedActivitiesTableComponent
